import { useAuth } from "@/auth/AuthProvider";
import {
  isBankLinkConnected,
  type BondBankLink,
  type BondBankLinkState,
} from "@/lib/brokerBonds";
import { useBankLinkState, type BondLinkPollOptions } from "@/lib/useBondLink";
import { CustomerLinkPanel } from "@/components/CustomerLinkPanel";

/**
 * The OTHER link the broker hands to their customer, who uses it to connect
 * their bank through Plaid (BRK-S118 / PMS-S259 / SUR-S100), and how far they
 * have got with it (BRK-S121 / PMS-S270 / SUR-S118).
 *
 * A SEPARATE CARD, not a second row on the accounting one: nothing gates this
 * link on the accounting step, so an order can carry either, both or neither,
 * and two independent facts do not belong under one heading. It is the SAME
 * component though — `CustomerLinkPanel` above — because two cards that look
 * alike and are written twice stop looking alike.
 *
 * THE PREDICTION HELD. BRK-S118 said connection state would arrive as an added
 * key and that this wrapper would then grow a `state`-driven choice exactly like
 * the accounting card's, without the shared card changing at all. That is what
 * happened: `CustomerLinkPanel` is untouched, and it still asks no link what
 * state it is in.
 *
 * TWO ABSENCES, TWO DIFFERENT FACTS, and conflating them is the one mistake
 * this component exists to avoid:
 *
 *   • NO `bankLink` — there is nothing to hand over. A draft, an order with no
 *     owner on file, a workflow with no consent step, a vendor outage and an
 *     order filed before the link existed are one fact to a broker, so this
 *     renders NOTHING: no empty card, no "not applicable" row, no "not
 *     connected yet" claiming knowledge nobody has.
 *   • a `bankLink` with NO `state` — an order minted before contract 5.5.0. The
 *     link is real and the customer's progress is UNKNOWN, so the card renders
 *     exactly as it did before this story: the url, and no status line.
 *     Defaulting the state to `pending` here would publish a claim about a
 *     customer nobody made, on every order filed before the key existed.
 */
export function BankLinkPanel({
  link,
  orderId,
  poll,
}: {
  link?: BondBankLink;
  /** The order to re-read while the state can still move — in hand wherever
   *  the card renders, exactly as for the accounting card. */
  orderId?: string;
  /** Injected in tests; defaults to a real timer. */
  poll?: BondLinkPollOptions;
}) {
  const { authedRequest } = useAuth();
  // The freshest state, not the one this surface was rendered with. Hooks run
  // before the absence check below — an absent link watches nothing.
  const current = useBankLinkState(authedRequest, orderId, link, poll);
  if (!current) return null;
  // A blank url is the same fact as no key: nothing to hand over. There is no
  // state that makes an empty string worth rendering, so the card goes away
  // rather than offering a copy control over one.
  const url = current.linkUrl.trim() !== "" ? current.linkUrl : null;
  if (url === null) return null;
  const words = bankLinkWords(current.state);

  return (
    <CustomerLinkPanel
      headingId="bank-link-heading"
      heading="Your customer’s bank connection"
      // OMITTED, not defaulted, when nothing is known — which is what keeps the
      // pre-5.5.0 rendering identical to what it was.
      {...(words.status === undefined
        ? {}
        : { status: { label: words.status, connected: words.connected } })}
      lead={words.lead}
      detail={words.detail}
      url={words.offerUrl ? url : null}
      copyLabel="Copy bank link"
    />
  );
}

/** One bank state's words. Mirrors `AccountingLinkWords`, with `status`
 *  OPTIONAL: this axis has a state that means "nobody has told us". */
interface BankLinkWords {
  /** The short status line, or `undefined` for a link whose progress is
   *  unknown — the pre-5.5.0 order, which gets no status row at all. */
  status?: string;
  /** Whether the status reads as connected (the quiet green indicator). */
  connected: boolean;
  lead: string;
  detail: string;
  offerUrl: boolean;
}

/**
 * The whole bank state → copy mapping, in ONE exhaustive place (BRK-S121). Every
 * member of the union gets a branch and the `never` check below proves it, so
 * "no state renders blank" is a compile-time property rather than a test we
 * remembered to write. A fifth member cannot be added upstream and silently
 * render as `pending` — the mirror would reject it first, and this would not
 * compile second.
 *
 * ONE INPUT, NOT TWO, unlike the accounting card's: `linkUrl` is not nullable
 * here, so there is no "minted but nothing to send" case to fold in. The one
 * state that withholds the url does so because of what the state MEANS, not
 * because of what the wire carries.
 *
 * FOUR STATES AND A SILENCE. `undefined` is the pre-5.5.0 order and is answered
 * FIRST, deliberately: it is the case a default would swallow, and the words it
 * gets are the ones this card used before it had a state at all.
 *
 * NONE OF THESE IS AN ERROR — the same line the accounting card holds (SUR-S052
 * answers 5, 7). The order is complete at submit and there is nothing the broker
 * can do if the customer never connects, so no state gets error styling, an
 * "incomplete" badge, a nag or a dismiss. And there is no state here that asks
 * the broker to DO anything beyond sending the link: Plaid-via-Lendflow
 * publishes no connection health, so this axis has no `needsReauth` and no
 * `disabled` to name a next step for.
 */
function bankLinkWords(state: BondBankLinkState | undefined): BankLinkWords {
  if (state === undefined) {
    // Nothing is known, so nothing is claimed. WORD FOR WORD what this card
    // said before BRK-S121, because that rendering was correct for exactly this
    // case and an order filed before 5.5.0 stays in it forever.
    return {
      connected: false,
      lead: "Send this link to your customer.",
      // Names its own system and nothing else. A cross-reference to the
      // neighbouring card ("separate from the accounting link…") reads as
      // helpful and puts the word `accounting` inside the BANK card, which is
      // the one place a broker skimming for the right url must not find it.
      detail:
        "They use it to connect their bank — nothing else is needed from you.",
      offerUrl: true,
    };
  }
  const connected = isBankLinkConnected(state);
  switch (state) {
    case "unminted":
      // PUBLISHED ON THE AXIS AND NEVER SENT WITH A KEY: the absence of the
      // whole `bankLink` is what says no link was minted, and discovery cannot
      // serialize this state beside a url. It is answered anyway — the mirror
      // accepts every member the contract publishes, so every member needs
      // words — and it WITHHOLDS the url, because a link that was never minted
      // is a leftover, and a broker who sends one sends their customer
      // somewhere that does not work. The words are the accounting card's for
      // the same state name, which is the point: one visual system.
      return {
        status: "Not connected",
        connected,
        lead: "We’re still preparing this link.",
        detail:
          "Your bond request is submitted and complete. The link to send your customer isn’t ready yet — check back on this submission shortly.",
        offerUrl: false,
      };
    case "pending":
      // The state most orders are in, and the one thing the broker can act on:
      // send it. Upstream cannot tell an unfinished connection from an
      // unobserved one, and publishes the earlier of the two — so this says
      // "waiting", never "they have not connected".
      return {
        status: "Not connected",
        connected,
        lead: "Waiting for your customer to connect.",
        detail:
          "Send this link to your customer. They use it to connect their bank — nothing else is needed from you.",
        offerUrl: true,
      };
    case "connected":
      // CONNECTED FIRST, because that is the news. The figures are a separate
      // orchestration upstream, so a customer who finished minutes ago may
      // still have none — this says the retrieval is happening and that nothing
      // is needed, rather than implying a delay somebody should chase.
      return {
        status: "Connected — retrieving bank figures",
        connected,
        lead: "Your customer has connected their bank.",
        detail:
          "Their bank figures are still coming across. Nothing else is needed from you.",
        offerUrl: true,
      };
    case "figuresReady":
      // Done. The link stays on the card — a customer who has to redo the
      // connection needs it, and there is no `needsReauth` on this axis to tell
      // us when that happens.
      return {
        status: "Connected",
        connected,
        lead: "Your customer has connected their bank.",
        detail:
          "Nothing else is needed from you. The link stays here if they ever need to reconnect.",
        offerUrl: true,
      };
    default: {
      // Unreachable: the union is closed and every member is answered above.
      const exhaustive: never = state;
      return exhaustive;
    }
  }
}
